import { useCallback, useEffect, useRef, useState } from 'react';
import Moodboard from '../MoodboardComponent';
import Tabs from '../Tabs/Tabs';
import Tab from '../Tabs/Tab';
import CustomDropdown from '../AdminReportComponents/CustomDropdown';
import { getDefaultSchoolYear } from '../reportUtilities';
import StudentsSummary from './StudentsSummary';
import StudentsElementary from './StudentsElementary';
import StudentsSecondary from './StudentsSecondary';
import styles from './TableStyles.module.scss';
import MoodboardMiddleSchool from '../../MoodboardMiddleSchool/MoodboardMiddleSchool';
import { Assignment } from '@peekapak/types/LessonPlanTypes';
import {
  PlatformEventsList,
  ReportStudent,
  ReportingMixpanelEvent,
  TeacherClassroomMaps,
} from '@peekapak/types/ReportDataExchangeTypes';
import { UserProfileType } from '@peekapak/types/DataProtocolTypes';

interface OverallUsageProps {
  errorMessage: string;
  gameProgressMap: Record<string, unknown>;
  gameProgressMapReady: boolean;
  gameProgressMapLoadProgress: string;
  assignmentsMap: Record<string, Assignment[]>;
  assignmentsMapReady: boolean;
  assignmentsMapLoadProgress?: string;
  journalsMap: Record<string, unknown>;
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  userList: UserProfileType[];
  legacyPlatformEvents: PlatformEventsList;
  legacyPlatformEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
}

type StudentAnalyticsProps = OverallUsageProps & {
  selectedSchool: string;
  selectedSchoolYear: string;
  tableOverride: string;
  startDate: Date | null;
  endDate: Date | null;
};

interface StudentAnalyticsState {
  moodboardSelectedTeacher: string;
  moodboardSelectedClassroom: string;
}

const StudentAnalytics = ({
  teacherStudentMapReady,
  teacherStudentMap,
  userList,
  selectedSchool,
  selectedSchoolYear,
  legacyPeekavilleEvents,
  legacyPeekavilleEventsReady,
  gameProgressMap,
  gameProgressMapReady,
  gameProgressMapLoadProgress,
  assignmentsMap,
  assignmentsMapReady,
  assignmentsMapLoadProgress,
  journalsMap,
  journalsMapReady,
  journalsMapLoadProgress,
  errorMessage,
  teacherClassroomMaps,
  teacherClassroomMapsReady,
  tableOverride,
  legacyPlatformEvents,
  legacyPlatformEventsReady,
  newMixpanelEvents,
  newMixpanelEventsReady,
  startDate,
  endDate,
}: StudentAnalyticsProps) => {
  const [myState, setMyState] = useState<StudentAnalyticsState>({
    moodboardSelectedTeacher: '',
    moodboardSelectedClassroom: '',
  });

  const currentlySelectedSchool = useRef();

  const hasSwitchedSchools = useCallback(() => {
    const result = currentlySelectedSchool.current !== selectedSchool;
    return result;
  }, [selectedSchool]);

  /*
  useEffect(() => {
    currentlySelectedSchool.current = selectedSchool;
  }, [selectedSchool]);
   */

  useEffect(() => {
    if (currentlySelectedSchool.current !== selectedSchool) {
      currentlySelectedSchool.current = selectedSchool;
      setMyState((prevState) => ({
        ...prevState,
        moodboardSelectedTeacher: '',
        moodboardSelectedClassroom: '',
      }));
    }
  }, [selectedSchool]);

  const teacherOptions =
    teacherClassroomMapsReady && teacherClassroomMaps?.maps
      ? teacherClassroomMaps.maps.map((teacher) => ({ id: teacher.userId, name: teacher.name }))
      : [];

  const selectedTeacher =
    teacherClassroomMapsReady && teacherClassroomMaps?.maps
      ? teacherClassroomMaps.maps.find((teacher) => teacher.userId === myState.moodboardSelectedTeacher)
      : null;

  const classroomOptions = selectedTeacher
    ? selectedTeacher.classrooms.map((classroom) => ({ id: classroom.classroomId, name: classroom.className }))
    : [];

  return (
    <div>
      <Tabs initialTab='Summary' secondary>
        <Tab label='Summary'>
          <StudentsSummary
            startDate={startDate}
            endDate={endDate}
            userList={userList}
            teacherStudentMap={teacherStudentMap}
            teacherStudentMapReady={teacherStudentMapReady}
            teacherClassroomMaps={teacherClassroomMaps}
            teacherClassroomMapsReady={teacherClassroomMapsReady}
            legacyPlatformEventsReady={legacyPlatformEventsReady}
            legacyPlatformEvents={legacyPlatformEvents}
            legacyPeekavilleEvents={legacyPeekavilleEvents}
            legacyPeekavilleEventsReady={legacyPeekavilleEventsReady}
            journalsMap={journalsMap}
            journalsMapReady={journalsMapReady}
            journalsMapLoadProgress={journalsMapLoadProgress}
            errorMessage={errorMessage}
            newMixpanelEvents={newMixpanelEvents}
            newMixpanelEventsReady={newMixpanelEventsReady}
            gameProgressMap={gameProgressMap}
            assignmentsMap={assignmentsMap}
          />
        </Tab>
        <Tab label='Elementary'>
          <StudentsElementary
            startDate={startDate}
            endDate={endDate}
            userList={userList}
            teacherStudentMap={teacherStudentMap}
            teacherStudentMapReady={teacherStudentMapReady}
            teacherClassroomMaps={teacherClassroomMaps}
            teacherClassroomMapsReady={teacherClassroomMapsReady}
            legacyPlatformEventsReady={legacyPlatformEventsReady}
            legacyPlatformEvents={legacyPlatformEvents}
            legacyPeekavilleEvents={legacyPeekavilleEvents}
            legacyPeekavilleEventsReady={legacyPeekavilleEventsReady}
            gameProgressMapReady={gameProgressMapReady}
            gameProgressMap={gameProgressMap}
            gameProgressMapLoadProgress={gameProgressMapLoadProgress}
            journalsMap={journalsMap}
            journalsMapReady={journalsMapReady}
            journalsMapLoadProgress={journalsMapLoadProgress}
            errorMessage={errorMessage}
            newMixpanelEvents={newMixpanelEvents}
            newMixpanelEventsReady={newMixpanelEventsReady}
          />
        </Tab>
        <Tab label='Secondary'>
          <StudentsSecondary
            startDate={startDate}
            endDate={endDate}
            assignmentsMap={assignmentsMap}
            assignmentsMapReady={assignmentsMapReady}
            assignmentsMapLoadProgress={assignmentsMapLoadProgress}
            userList={userList}
            teacherStudentMap={teacherStudentMap}
            teacherStudentMapReady={teacherStudentMapReady}
            teacherClassroomMaps={teacherClassroomMaps}
            teacherClassroomMapsReady={teacherClassroomMapsReady}
            legacyPlatformEventsReady={legacyPlatformEventsReady}
            legacyPlatformEvents={legacyPlatformEvents}
            legacyPeekavilleEvents={legacyPeekavilleEvents}
            legacyPeekavilleEventsReady={legacyPeekavilleEventsReady}
            journalsMap={journalsMap}
            journalsMapReady={journalsMapReady}
            journalsMapLoadProgress={journalsMapLoadProgress}
            errorMessage={errorMessage}
            newMixpanelEvents={newMixpanelEvents}
            newMixpanelEventsReady={newMixpanelEventsReady}
          />
        </Tab>
        <Tab label='Moodboard'>
          {!hasSwitchedSchools() && currentlySelectedSchool.current && selectedSchoolYear === getDefaultSchoolYear() ? (
            <div className={styles.selectorLine}>
              <div
                style={{
                  visibility: teacherClassroomMapsReady ? 'visible' : 'hidden',
                }}
              >
                {teacherOptions.length > 0 && (
                  <CustomDropdown
                    list={teacherOptions}
                    onSelectSubject={onStudentAnalyticsTeacherChange}
                    initialValue={teacherOptions[0].id}
                    currentlySelectedItem={myState.moodboardSelectedTeacher}
                    disableChangeLocation={false}
                    icon='ph:chalkboard-teacher'
                    placeholder='Select a teacher'
                  />
                )}
              </div>
              <div
                style={{
                  visibility: myState.moodboardSelectedTeacher && teacherClassroomMapsReady ? 'visible' : 'hidden',
                }}
              >
                {classroomOptions.length > 0 && (
                  <CustomDropdown
                    list={classroomOptions}
                    onSelectSubject={onStudentAnalyticsClassroomChange}
                    initialValue={classroomOptions[0].id}
                    currentlySelectedItem={myState.moodboardSelectedClassroom}
                    disableChangeLocation={false}
                    icon='icon-park-outline:classroom'
                    placeholder='Select one of this teacher’s classrooms'
                  />
                )}
              </div>
            </div>
          ) : null}
          {teacherClassroomMapsReady && myState.moodboardSelectedTeacher && myState.moodboardSelectedClassroom && (
            <div>
              {parseInt(
                teacherClassroomMaps.maps
                  .filter((t) => t.userId === myState.moodboardSelectedTeacher)[0]
                  .classrooms.filter((c) => c.classroomId === myState.moodboardSelectedClassroom)[0].grade,
              ) < 6 ? (
                <Moodboard
                  classroomId={myState.moodboardSelectedClassroom}
                  userId={myState.moodboardSelectedTeacher}
                  tableOverride={tableOverride}
                />
              ) : (
                <MoodboardMiddleSchool
                  classroomProps={
                    teacherClassroomMaps.maps
                      .filter((t) => t.userId === myState.moodboardSelectedTeacher)[0]
                      .classrooms.filter((c) => c.classroomId === myState.moodboardSelectedClassroom)[0]
                  }
                />
              )}
            </div>
          )}
        </Tab>
      </Tabs>
    </div>
  );

  function onStudentAnalyticsClassroomChange(value: string | null) {
    setMyState((prevState) => ({
      ...prevState,
      moodboardSelectedClassroom: value || '',
    }));
  }

  function onStudentAnalyticsTeacherChange(value: string | null) {
    setMyState((prevState) => ({
      ...prevState,
      moodboardSelectedTeacher: value || '',
      moodboardSelectedClassroom: '',
    }));
  }
};

export default StudentAnalytics;
